import { useState, useMemo } from 'react';
import {
  X, GitCompare, Plus, Minus, Edit2,
  ChevronDown, ChevronRight, Database, Link2,
  Hash, Tag, AlignLeft, Key, Layers
} from 'lucide-react';

// ─── helpers ────────────────────────────────────────────────────────────────

const nodeLabel = (node) => node?.data?.name || node?.id || '?';

const edgeLabel = (edge, nodesById) => {
  const src = nodesById[edge.source]?.data?.name || edge.source;
  const tgt = nodesById[edge.target]?.data?.name || edge.target;
  const card = edge.data?.cardinality ? ` [${edge.data.cardinality}]` : '';
  return `${src} → ${tgt}${card}`;
};

const arrEq = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Returns list of change-descriptor objects for a single node pair
const compareNodeData = (vNode, cNode) => {
  const changes = [];

  // Name
  if (vNode.data?.name !== cNode.data?.name) {
    changes.push({ kind: 'name', label: 'Name', from: vNode.data?.name, to: cNode.data?.name });
  }

  // Description
  if ((vNode.data?.description || '') !== (cNode.data?.description || '')) {
    changes.push({ kind: 'description', label: 'Description', from: vNode.data?.description || '—', to: cNode.data?.description || '—' });
  }

  // Color
  if ((vNode.data?.color || '') !== (cNode.data?.color || '')) {
    changes.push({ kind: 'color', label: 'Color', from: vNode.data?.color || '—', to: cNode.data?.color || '—' });
  }

  // Type (table/view/fact/dimension/…)
  if (vNode.type !== cNode.type) {
    changes.push({ kind: 'type', label: 'Entity type', from: vNode.type, to: cNode.type });
  }

  // Tags
  const vTags = (vNode.data?.tags || []).map(t => t.text).sort();
  const cTags = (cNode.data?.tags || []).map(t => t.text).sort();
  if (!arrEq(vTags, cTags)) {
    changes.push({ kind: 'tags', label: 'Tags', from: vTags.join(', ') || '—', to: cTags.join(', ') || '—' });
  }

  // Fields
  const vFields = vNode.data?.fields || [];
  const cFields = cNode.data?.fields || [];
  const vFieldsByName = Object.fromEntries(vFields.map(f => [f.name, f]));
  const cFieldsByName = Object.fromEntries(cFields.map(f => [f.name, f]));

  const addedFields = cFields.filter(f => !vFieldsByName[f.name]);
  const removedFields = vFields.filter(f => !cFieldsByName[f.name]);
  const modifiedFields = cFields
    .filter(f => vFieldsByName[f.name])
    .map(f => {
      const vf = vFieldsByName[f.name];
      const fieldChanges = [];
      if (vf.type !== f.type) fieldChanges.push({ prop: 'Type', from: vf.type, to: f.type });
      if (!!vf.nullable !== !!f.nullable) fieldChanges.push({ prop: 'Nullable', from: String(!!vf.nullable), to: String(!!f.nullable) });
      if (!!vf.isPrimaryKey !== !!f.isPrimaryKey) fieldChanges.push({ prop: 'Primary Key', from: String(!!vf.isPrimaryKey), to: String(!!f.isPrimaryKey) });
      if (!!vf.isForeignKey !== !!f.isForeignKey) fieldChanges.push({ prop: 'Foreign Key', from: String(!!vf.isForeignKey), to: String(!!f.isForeignKey) });
      if (!!vf.unique !== !!f.unique) fieldChanges.push({ prop: 'Unique', from: String(!!vf.unique), to: String(!!f.unique) });
      if (!!vf.autoIncrement !== !!f.autoIncrement) fieldChanges.push({ prop: 'Auto Increment', from: String(!!vf.autoIncrement), to: String(!!f.autoIncrement) });
      const vDefault = vf.defaultValue == null ? '' : String(vf.defaultValue);
      const cDefault = f.defaultValue == null ? '' : String(f.defaultValue);
      if (vDefault !== cDefault) fieldChanges.push({ prop: 'Default', from: vDefault || '—', to: cDefault || '—' });
      if ((vf.description || '') !== (f.description || '')) fieldChanges.push({ prop: 'Description', from: vf.description || '—', to: f.description || '—' });
      return fieldChanges.length > 0 ? { name: f.name, fieldChanges } : null;
    })
    .filter(Boolean);

  if (addedFields.length || removedFields.length || modifiedFields.length) {
    changes.push({ kind: 'fields', label: 'Fields', addedFields, removedFields, modifiedFields });
  }

  // Indexes
  const vIndexes = vNode.data?.indexes || [];
  const cIndexes = cNode.data?.indexes || [];
  const vIndexById = Object.fromEntries(vIndexes.map(i => [i.id, i]));
  const cIndexById = Object.fromEntries(cIndexes.map(i => [i.id, i]));

  const addedIndexes = cIndexes.filter(i => !vIndexById[i.id]);
  const removedIndexes = vIndexes.filter(i => !cIndexById[i.id]);
  const modifiedIndexes = cIndexes
    .filter(i => vIndexById[i.id] && !arrEq(i, vIndexById[i.id]))
    .map(i => ({ current: i, version: vIndexById[i.id] }));

  if (addedIndexes.length || removedIndexes.length || modifiedIndexes.length) {
    changes.push({ kind: 'indexes', label: 'Indexes', addedIndexes, removedIndexes, modifiedIndexes });
  }

  return changes;
};

// Compare edges
const compareEdgeData = (vEdge, cEdge) => {
  const changes = [];
  if ((vEdge.data?.label || '') !== (cEdge.data?.label || ''))
    changes.push({ prop: 'Label', from: vEdge.data?.label || '—', to: cEdge.data?.label || '—' });
  if ((vEdge.data?.cardinality || '') !== (cEdge.data?.cardinality || ''))
    changes.push({ prop: 'Cardinality', from: vEdge.data?.cardinality || '—', to: cEdge.data?.cardinality || '—' });
  if ((vEdge.data?.sourceField || '') !== (cEdge.data?.sourceField || ''))
    changes.push({ prop: 'Source field', from: vEdge.data?.sourceField || '—', to: cEdge.data?.sourceField || '—' });
  if ((vEdge.data?.targetField || '') !== (cEdge.data?.targetField || ''))
    changes.push({ prop: 'Target field', from: vEdge.data?.targetField || '—', to: cEdge.data?.targetField || '—' });
  if (vEdge.type !== cEdge.type)
    changes.push({ prop: 'Relationship type', from: vEdge.type, to: cEdge.type });
  return changes;
};

// ─── sub-components ─────────────────────────────────────────────────────────

const Badge = ({ type, count }) => {
  if (!count) return null;
  const styles = {
    added: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    removed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    modified: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  };
  const icons = { added: <Plus size={10} />, removed: <Minus size={10} />, modified: <Edit2 size={10} /> };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[type]}`}>
      {icons[type]} {count}
    </span>
  );
};

const PropRow = ({ prop, from, to }) => (
  <div className="flex items-start gap-2 text-xs py-1">
    <span className="text-gray-500 dark:text-gray-400 w-28 flex-shrink-0">{prop}:</span>
    <span className="line-through text-red-600 dark:text-red-400 break-all">{from}</span>
    <span className="text-gray-400">→</span>
    <span className="text-green-600 dark:text-green-400 break-all">{to}</span>
  </div>
);

const FieldChip = ({ field, variant }) => {
  const colors = {
    added: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300',
    removed: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300',
  };
  const icons = { added: <Plus size={10} />, removed: <Minus size={10} /> };
  return (
    <span className={`inline-flex items-center gap-1 border rounded px-2 py-0.5 text-xs ${colors[variant]}`}>
      {icons[variant]} {field.name} <span className="opacity-60">({field.type})</span>
    </span>
  );
};

const IndexChip = ({ index, variant }) => {
  const colors = {
    added: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300',
    removed: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300',
  };
  const icons = { added: <Plus size={10} />, removed: <Minus size={10} /> };
  return (
    <span className={`inline-flex items-center gap-1 border rounded px-2 py-0.5 text-xs ${colors[variant]}`}>
      {icons[variant]} {index.name} <span className="opacity-60">({index.type})</span>
    </span>
  );
};

const SectionBlock = ({ title, children, isEmpty }) => {
  if (isEmpty) return null;
  return (
    <div className="mt-2 pl-2 border-l-2 border-gray-200 dark:border-gray-600">
      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">{title}</p>
      {children}
    </div>
  );
};

// Single entity row in the diff list
const EntityDiffRow = ({ item, status }) => {
  const [open, setOpen] = useState(false);

  const statusStyles = {
    added: 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10',
    removed: 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10',
    modified: 'border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/10',
  };
  const statusIcons = {
    added: <Plus size={14} className="text-green-600 dark:text-green-400" />,
    removed: <Minus size={14} className="text-red-600 dark:text-red-400" />,
    modified: <Edit2 size={14} className="text-yellow-600 dark:text-yellow-400" />,
  };

  const hasDetail = status === 'modified' && item.changes?.length > 0;
  const name = status === 'modified' ? nodeLabel(item.currentNode) : nodeLabel(item);
  const originalName = status === 'modified' && item.currentNode?.data?.name !== item.versionNode?.data?.name
    ? item.versionNode?.data?.name
    : null;

  return (
    <div className={`border rounded-lg mb-2 overflow-hidden ${statusStyles[status]}`}>
      <div
        className={`flex items-center gap-2 px-3 py-2 ${hasDetail ? 'cursor-pointer' : ''}`}
        onClick={() => hasDetail && setOpen(o => !o)}
      >
        {statusIcons[status]}
        <span className="font-medium text-sm text-gray-900 dark:text-white flex-1">
          {name}
          {originalName && <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">(was: {originalName})</span>}
        </span>
        {status === 'added' && <span className="text-xs text-gray-500 dark:text-gray-400">New entity</span>}
        {status === 'removed' && <span className="text-xs text-gray-500 dark:text-gray-400">Removed entity</span>}
        {status === 'modified' && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {item.changes.length} change{item.changes.length !== 1 ? 's' : ''}
          </span>
        )}
        {hasDetail && (open
          ? <ChevronDown size={14} className="text-gray-400" />
          : <ChevronRight size={14} className="text-gray-400" />)}
      </div>

      {hasDetail && open && (
        <div className="px-3 pb-3 border-t border-yellow-200 dark:border-yellow-800 space-y-2">
          {item.changes.map((change, ci) => (
            <div key={ci} className="mt-2">
              {/* Simple property changes */}
              {['name', 'description', 'color', 'type', 'tags'].includes(change.kind) && (
                <PropRow prop={change.label} from={change.from} to={change.to} />
              )}

              {/* Fields */}
              {change.kind === 'fields' && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 flex items-center gap-1 mb-1">
                    <Key size={11} /> Fields
                  </p>
                  <SectionBlock title="Added" isEmpty={!change.addedFields.length}>
                    <div className="flex flex-wrap gap-1">
                      {change.addedFields.map(f => <FieldChip key={f.name} field={f} variant="added" />)}
                    </div>
                  </SectionBlock>
                  <SectionBlock title="Removed" isEmpty={!change.removedFields.length}>
                    <div className="flex flex-wrap gap-1">
                      {change.removedFields.map(f => <FieldChip key={f.name} field={f} variant="removed" />)}
                    </div>
                  </SectionBlock>
                  <SectionBlock title="Modified" isEmpty={!change.modifiedFields.length}>
                    {change.modifiedFields.map(mf => (
                      <div key={mf.name} className="mb-1">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{mf.name}</p>
                        {mf.fieldChanges.map((fc, i) => <PropRow key={i} prop={fc.prop} from={fc.from} to={fc.to} />)}
                      </div>
                    ))}
                  </SectionBlock>
                </div>
              )}

              {/* Indexes */}
              {change.kind === 'indexes' && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 flex items-center gap-1 mb-1">
                    <Hash size={11} /> Indexes
                  </p>
                  <SectionBlock title="Added" isEmpty={!change.addedIndexes.length}>
                    <div className="flex flex-wrap gap-1">
                      {change.addedIndexes.map(i => <IndexChip key={i.id} index={i} variant="added" />)}
                    </div>
                  </SectionBlock>
                  <SectionBlock title="Removed" isEmpty={!change.removedIndexes.length}>
                    <div className="flex flex-wrap gap-1">
                      {change.removedIndexes.map(i => <IndexChip key={i.id} index={i} variant="removed" />)}
                    </div>
                  </SectionBlock>
                  <SectionBlock title="Modified" isEmpty={!change.modifiedIndexes.length}>
                    {change.modifiedIndexes.map(({ current, version }) => (
                      <div key={current.id} className="text-xs text-gray-600 dark:text-gray-400">
                        {current.name}: columns changed
                      </div>
                    ))}
                  </SectionBlock>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Single edge/relationship row
const EdgeDiffRow = ({ item, status, nodesById }) => {
  const [open, setOpen] = useState(false);

  const statusStyles = {
    added: 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10',
    removed: 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10',
    modified: 'border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/10',
  };
  const statusIcons = {
    added: <Plus size={14} className="text-green-600 dark:text-green-400" />,
    removed: <Minus size={14} className="text-red-600 dark:text-red-400" />,
    modified: <Edit2 size={14} className="text-yellow-600 dark:text-yellow-400" />,
  };

  const edge = status === 'modified' ? item.currentEdge : item;
  const label = edgeLabel(edge, nodesById);
  const hasDetail = status === 'modified' && item.changes?.length > 0;

  return (
    <div className={`border rounded-lg mb-2 overflow-hidden ${statusStyles[status]}`}>
      <div
        className={`flex items-center gap-2 px-3 py-2 ${hasDetail ? 'cursor-pointer' : ''}`}
        onClick={() => hasDetail && setOpen(o => !o)}
      >
        {statusIcons[status]}
        <span className="font-medium text-sm text-gray-900 dark:text-white flex-1">{label}</span>
        {status === 'added' && <span className="text-xs text-gray-500 dark:text-gray-400">New relationship</span>}
        {status === 'removed' && <span className="text-xs text-gray-500 dark:text-gray-400">Removed relationship</span>}
        {status === 'modified' && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {item.changes.length} change{item.changes.length !== 1 ? 's' : ''}
          </span>
        )}
        {hasDetail && (open
          ? <ChevronDown size={14} className="text-gray-400" />
          : <ChevronRight size={14} className="text-gray-400" />)}
      </div>
      {hasDetail && open && (
        <div className="px-3 pb-3 border-t border-yellow-200 dark:border-yellow-800">
          {item.changes.map((c, i) => <PropRow key={i} prop={c.prop} from={c.from} to={c.to} />)}
        </div>
      )}
    </div>
  );
};

// ─── main modal ─────────────────────────────────────────────────────────────

const VersionCompareModal = ({ version, currentNodes, currentEdges, onClose }) => {
  const [activeTab, setActiveTab] = useState('entities');

  // Build maps for fast lookups using all nodes (version + current)
  const allNodesById = useMemo(() => {
    const map = {};
    (version.snapshot.nodes || []).forEach(n => { map[n.id] = n; });
    (currentNodes || []).forEach(n => { map[n.id] = n; });
    return map;
  }, [version, currentNodes]);

  // ── Entity diff ──────────────────────────────────────────────
  const nodeDiff = useMemo(() => {
    const vNodes = (version.snapshot.nodes || []).filter(n => n.type !== 'text');
    const cNodes = (currentNodes || []).filter(n => n.type !== 'text');

    const vById = Object.fromEntries(vNodes.map(n => [n.id, n]));
    const cById = Object.fromEntries(cNodes.map(n => [n.id, n]));

    const added = cNodes.filter(n => !vById[n.id]);
    const removed = vNodes.filter(n => !cById[n.id]);

    const modified = vNodes
      .filter(n => cById[n.id])
      .map(vNode => {
        const cNode = cById[vNode.id];
        const changes = compareNodeData(vNode, cNode);
        return changes.length > 0 ? { id: vNode.id, versionNode: vNode, currentNode: cNode, changes } : null;
      })
      .filter(Boolean);

    return { added, removed, modified };
  }, [version, currentNodes]);

  // ── Edge diff ────────────────────────────────────────────────
  const edgeDiff = useMemo(() => {
    const vEdges = version.snapshot.edges || [];
    const cEdges = currentEdges || [];

    const vById = Object.fromEntries(vEdges.map(e => [e.id, e]));
    const cById = Object.fromEntries(cEdges.map(e => [e.id, e]));

    const added = cEdges.filter(e => !vById[e.id]);
    const removed = vEdges.filter(e => !cById[e.id]);

    const modified = vEdges
      .filter(e => cById[e.id])
      .map(vEdge => {
        const cEdge = cById[vEdge.id];
        const changes = compareEdgeData(vEdge, cEdge);
        return changes.length > 0 ? { id: vEdge.id, versionEdge: vEdge, currentEdge: cEdge, changes } : null;
      })
      .filter(Boolean);

    return { added, removed, modified };
  }, [version, currentEdges]);

  const totalChanges =
    nodeDiff.added.length + nodeDiff.removed.length + nodeDiff.modified.length +
    edgeDiff.added.length + edgeDiff.removed.length + edgeDiff.modified.length;

  const entityChanges = nodeDiff.added.length + nodeDiff.removed.length + nodeDiff.modified.length;
  const relChanges = edgeDiff.added.length + edgeDiff.removed.length + edgeDiff.modified.length;

  const tabs = [
    { id: 'entities', label: 'Entities', icon: <Database size={14} />, count: entityChanges },
    { id: 'relationships', label: 'Relationships', icon: <Link2 size={14} />, count: relChanges },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[55] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <GitCompare size={20} className="text-purple-600 dark:text-purple-400" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Compare: Version #{version.versionNumber} vs Current
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                "{version.description}"
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-500 dark:text-gray-400"
          >
            <X size={20} />
          </button>
        </div>

        {/* Summary bar */}
        <div className="flex items-center gap-4 px-5 py-3 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
          {totalChanges === 0 ? (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              No differences found — the model is identical to this version.
            </span>
          ) : (
            <>
              <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                {totalChanges} change{totalChanges !== 1 ? 's' : ''} detected:
              </span>
              <Badge type="added" count={nodeDiff.added.length + edgeDiff.added.length} />
              <Badge type="removed" count={nodeDiff.removed.length + edgeDiff.removed.length} />
              <Badge type="modified" count={nodeDiff.modified.length + edgeDiff.modified.length} />
            </>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-5">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tab.id
                  ? 'border-purple-600 text-purple-600 dark:text-purple-400 dark:border-purple-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {tab.icon}
              {tab.label}
              {tab.count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === 'entities' && (
            <div>
              {entityChanges === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
                  <Database size={40} className="mb-3 opacity-40" />
                  <p className="text-sm">No entity changes between this version and the current model.</p>
                </div>
              ) : (
                <>
                  {nodeDiff.added.length > 0 && (
                    <div className="mb-4">
                      <h3 className="flex items-center gap-2 text-sm font-semibold text-green-700 dark:text-green-400 mb-2">
                        <Plus size={14} /> Added entities ({nodeDiff.added.length})
                      </h3>
                      {nodeDiff.added.map(n => <EntityDiffRow key={n.id} item={n} status="added" />)}
                    </div>
                  )}
                  {nodeDiff.removed.length > 0 && (
                    <div className="mb-4">
                      <h3 className="flex items-center gap-2 text-sm font-semibold text-red-700 dark:text-red-400 mb-2">
                        <Minus size={14} /> Removed entities ({nodeDiff.removed.length})
                      </h3>
                      {nodeDiff.removed.map(n => <EntityDiffRow key={n.id} item={n} status="removed" />)}
                    </div>
                  )}
                  {nodeDiff.modified.length > 0 && (
                    <div className="mb-4">
                      <h3 className="flex items-center gap-2 text-sm font-semibold text-yellow-700 dark:text-yellow-400 mb-2">
                        <Edit2 size={14} /> Modified entities ({nodeDiff.modified.length})
                      </h3>
                      {nodeDiff.modified.map(item => <EntityDiffRow key={item.id} item={item} status="modified" />)}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'relationships' && (
            <div>
              {relChanges === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
                  <Link2 size={40} className="mb-3 opacity-40" />
                  <p className="text-sm">No relationship changes between this version and the current model.</p>
                </div>
              ) : (
                <>
                  {edgeDiff.added.length > 0 && (
                    <div className="mb-4">
                      <h3 className="flex items-center gap-2 text-sm font-semibold text-green-700 dark:text-green-400 mb-2">
                        <Plus size={14} /> Added relationships ({edgeDiff.added.length})
                      </h3>
                      {edgeDiff.added.map(e => <EdgeDiffRow key={e.id} item={e} status="added" nodesById={allNodesById} />)}
                    </div>
                  )}
                  {edgeDiff.removed.length > 0 && (
                    <div className="mb-4">
                      <h3 className="flex items-center gap-2 text-sm font-semibold text-red-700 dark:text-red-400 mb-2">
                        <Minus size={14} /> Removed relationships ({edgeDiff.removed.length})
                      </h3>
                      {edgeDiff.removed.map(e => <EdgeDiffRow key={e.id} item={e} status="removed" nodesById={allNodesById} />)}
                    </div>
                  )}
                  {edgeDiff.modified.length > 0 && (
                    <div className="mb-4">
                      <h3 className="flex items-center gap-2 text-sm font-semibold text-yellow-700 dark:text-yellow-400 mb-2">
                        <Edit2 size={14} /> Modified relationships ({edgeDiff.modified.length})
                      </h3>
                      {edgeDiff.modified.map(item => <EdgeDiffRow key={item.id} item={item} status="modified" nodesById={allNodesById} />)}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default VersionCompareModal;
